import { fetchBaseQuery, createApi, FetchArgs, BaseQueryApi, BaseQueryFn } from "@reduxjs/toolkit/query/react";
import axios, { AxiosRequestConfig, AxiosProgressEvent } from "axios";

// import { RootState } from "../index";

export interface XhrBaseQueryArgs {
	url: string;
	method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
	body?: FormData | undefined;
	onUploadProgress?: (pct: number) => void;
	headers?: Record<string, string>;
	anonymous_token?: string;
}

export interface AxiosBaseQueryArgs {
	url: string;
	method: AxiosRequestConfig["method"];
	data?: AxiosRequestConfig["data"];
	params?: AxiosRequestConfig["params"];
	onUploadProgress?: (event: AxiosProgressEvent) => void;
}

// ----- axios

export const axiosBaseQuery =
	({ baseUrl = "" }: { baseUrl?: string } = {}): BaseQueryFn<AxiosBaseQueryArgs, unknown, unknown> =>
	async ({ url, method, data, params, onUploadProgress }) => {
		const token = localStorage.getItem("token");
		try {
			const result = await axios({
				url: baseUrl + url,
				method,
				data,
				params,
				onUploadProgress,
				withCredentials: true,
				headers: {
					authentication: token ? `Bearer ${token}` : undefined
				}
			});

			return { data: result.data };
		} catch (axiosError: any) {
			if (axiosError.error && axiosError.error.status === 401) {
				// Handle the 401 error here, e.g., redirecting to login
				localStorage.clear();
				window.location.href = "/login";
			}
			return {
				error: {
					status: axiosError.response?.status,
					data: axiosError.response?.data || axiosError.message
				}
			};
		}
	};

export const xhrBaseQuery =
	({ baseUrl }: { baseUrl?: string }): BaseQueryFn<XhrBaseQueryArgs, unknown, unknown> =>
	async ({ url, method = "GET", body, headers = {}, onUploadProgress, anonymous_token }) => {
		return new Promise(resolve => {
			const xhr = new XMLHttpRequest();
			xhr.open(method, baseUrl + url);

			// Send cookies if needed
			xhr.withCredentials = true;
			const token = localStorage.getItem("token");

			const finalHeaders: Record<string, string> = {
				...headers
			};

			if (anonymous_token) {
				finalHeaders["authentication"] = anonymous_token;
			} else if (token) {
				finalHeaders["authentication"] = `Bearer ${token}`;
			}

			Object.entries(finalHeaders).forEach(([key, value]) => {
				if (value !== undefined) {
					xhr.setRequestHeader(key, value);
				}
			});

			// Upload progress
			if (xhr.upload && onUploadProgress) {
				xhr.upload.onprogress = e => {
					if (e.lengthComputable) {
						const pct = Math.round((e.loaded * 100) / e.total);
						onUploadProgress(pct);
					}
				};
			}

			xhr.onload = () => {
				try {
					resolve({ data: JSON.parse(xhr.responseText) });
				} catch (err) {
					resolve({ error: { status: xhr.status, data: xhr.responseText } });
				}
			};

			xhr.onerror = () => {
				resolve({
					error: { status: xhr.status, data: "Network error" }
				});
			};

			xhr.send(body);
		});
	};

export const baseEndPoint = process.env.REACT_APP_BACKEND_API;
export const baseQuery = fetchBaseQuery({
	baseUrl: baseEndPoint,
	prepareHeaders: (headers, { getState }) => {
		// By default, if we have a token in the store, let's use that for authenticated requests
		const token = localStorage.getItem("token");
		if (token) {
			headers.set("authentication", `Bearer ${token}`);
		}
		return headers;
	}
});

const baseQueryWithAuth = async (args: FetchArgs, api: BaseQueryApi, extraOptions: {}) => {
	const result = await baseQuery(args, api, extraOptions);
	if (result.error && result.error.status === 401) {
		// Handle the 401 error here, e.g., redirecting to login
		localStorage.clear();
		window.location.href = "/login";
	}
	return result;
};

export const api = createApi({
	reducerPath: "api",
	keepUnusedDataFor: 30,
	baseQuery: baseQueryWithAuth,
	tagTypes: [
		"Invitations",
		"managed_invitation",
		"managed_invitation_gallery",
		"getAdminWeddingInvitations",
		"invitation_comments",
		"invitation_comment",
		"invitation_memories",
		"manage_invitation_memories",
		"notificationsSubscription",
		"notificationsSubscriptions",
		"userAffiliate",
		"userAffiliateCoupon",
		"adminAffiliateUsers",
		"userAffiliateSales",
		"adminSettlements",
		"userAffiliateTransfers",
		"adminAffiliateStats",
		"userAffiliateStats",
		"userAffiliateSalesByMonth"
	],
	endpoints: () => ({})
});

export const fileApi = createApi({
	reducerPath: "fileApi",
	keepUnusedDataFor: 30,
	// baseQuery: xhrBaseQuery({ baseUrl: baseEndPoint }),
	baseQuery: fetchBaseQuery({
		baseUrl: baseEndPoint,
		prepareHeaders: (headers, { getState }) => {
			// By default, if we have a token in the store, let's use that for authenticated requests
			const token = localStorage.getItem("token");
			if (token) {
				headers.set("authentication", `Bearer ${token}`);
			}

			return headers;
		}
	}),
	endpoints: () => ({})
});

export const buildQueryParams = (
	params: Record<string, string | number | boolean | null | undefined | Record<string, number>>
): string => {
	const query = Object.entries(params)
		.filter(([_, value]) => value !== undefined && value !== null)
		.map(([key, value]) => {
			if (value && typeof value === "object" && !Array.isArray(value)) {
				const nested = Object.entries(value)
					.map(([k, v]) => `${k}:${v}`)
					.join(",");
				return `${encodeURIComponent(key)}=${encodeURIComponent(nested)}`;
			}
			return `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`;
		})
		.join("&");

	return query ? `?${query}` : "";
};

export interface FilterOptions {
	page?: number;
	perPage?: number;
	totalItems?: number;
	sortBy?: Record<string, number>;
}

export interface FilterOptionsResponse<T> extends FilterOptions {
	items: T;
}

export interface ApiResponse<T> {
	data: T;
	success: boolean;
}
